import Image from "next/image"
import { useEffect, useState } from "react"
import { Button, Card, Stack, Typography, Visibility } from "@octopus-energy/coral/atoms"
import { IconCross } from "@octopus-energy/icons"
import { useTranslations } from "next-intl"
import { type OnboardingRouteNames } from "@/routes/onboarding"
import {
    DEFAULT_STICKY_REINSURANCE_CONFIG,
    STICKY_REINSURANCE_CONFIG_BY_STEP,
} from "./constants/stickyReinsuranceConfig"
import {
    CardWrapper,
    DismissButtonWrapper,
    MobileContainer,
    PeekOverlay,
    StickyWrapper,
} from "./StickyReinsurances.style"

type CardState = "entering" | "visible" | "dismissing" | "peeked"

type StickyReinsurancesProps = {
    stepName: OnboardingRouteNames
}

export function StickyReinsurances({ stepName }: StickyReinsurancesProps) {
    const t = useTranslations("StickyReinsurances")

    const config = STICKY_REINSURANCE_CONFIG_BY_STEP[stepName] ?? DEFAULT_STICKY_REINSURANCE_CONFIG

    const [cardState, setCardState] = useState<CardState>("entering")
    const [hovered, setHovered] = useState(false)

    useEffect(() => {
        setCardState("entering")
        requestAnimationFrame(() => setCardState("visible"))
    }, [stepName])

    const isPeeked = cardState === "peeked"

    const transforms: Record<CardState, string> = {
        entering: "translateX(120%)",
        visible: "translateX(0)",
        dismissing: "translateX(120%)",
        peeked: hovered ? "translateX(calc(100% - 8px))" : "translateX(100%)",
    }

    const opacities: Record<CardState, number> = {
        entering: 0,
        visible: 1,
        dismissing: 0,
        peeked: 1,
    }

    const transitions: Record<CardState, string> = {
        entering: "none",
        visible: "transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease-out",
        dismissing: "transform 0.3s ease-in, opacity 0.3s ease-in",
        peeked: "transform 0.2s ease-out, opacity 0.3s ease-out",
    }

    const dismiss = () => setCardState("dismissing")
    const reveal = () => setCardState("visible")

    const toggle = () => {
        const nextStateByCardState: Partial<Record<CardState, () => void>> = {
            visible: dismiss,
            peeked: reveal,
        }

        nextStateByCardState[cardState]?.()
    }

    return (
        <>
            <Visibility display={{ md: "none" }}>
                <MobileContainer>
                    <Stack alignItems="center">
                        <Image
                            src={config.image}
                            alt={t(`${config.translationKey}.alt`)}
                            width={68}
                            height={68}
                            style={{ flexShrink: 0, width: "50px", height: "auto" }}
                        />
                        <Typography textStyle="caption">{`${t(`${config.translationKey}.text`)}`}</Typography>
                    </Stack>
                </MobileContainer>
            </Visibility>
            <Visibility display={{ base: "none", md: "contents" }}>
                <StickyWrapper>
                    <CardWrapper
                        style={{
                            transform: transforms[cardState],
                            opacity: opacities[cardState],
                            transition: transitions[cardState],
                        }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={toggle}
                        onTransitionEnd={(e) => {
                            if (e.propertyName === "transform" && cardState === "dismissing") {
                                setHovered(false)
                                setCardState("peeked")
                            }
                        }}
                    >
                        <PeekOverlay isPeeked={isPeeked} />
                        {hovered && cardState === "visible" && (
                            <DismissButtonWrapper>
                                <Button
                                    variant="text"
                                    size="xs"
                                    onClick={dismiss}
                                    aria-label={t("dismiss")}
                                >
                                    <IconCross size={10} />
                                </Button>
                            </DismissButtonWrapper>
                        )}
                        <Card
                            borderless
                            padding="xsmall"
                            theme="secondary900"
                        >
                            <Stack alignItems="center">
                                <Image
                                    src={config.image}
                                    alt={t(`${config.translationKey}.alt`)}
                                    width={68}
                                    height={68}
                                    style={{ flexShrink: 0, width: "50px", height: "auto" }}
                                />
                                <Typography textStyle="caption">
                                    {`${t(`${config.translationKey}.text`)}`}
                                </Typography>
                            </Stack>
                        </Card>
                    </CardWrapper>
                </StickyWrapper>
            </Visibility>
        </>
    )
}

StickyReinsurances.messages = ["StickyReinsurances"] as const
